import { useEffect, useState } from "react";
import { ExternalLink, Mic, AlertCircle, CheckCircle2, Loader2, ListChecks } from "lucide-react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * VoiceScreeningEmbed — Streamlit-hosted screening tool embedded on the
 * logged-in patient dashboard. Replaces the old hero CTA and lets users
 * record/upload a voice sample without leaving the Manodayam shell.
 *
 * Post-screening bridge:
 *  - Listens for `window.postMessage({type: "manodayam:screening_result",
 *    risk_score, band?, category?, summary?, model_version?})` so a
 *    future Streamlit build can auto-log results to the timeline.
 *  - Any origin is accepted (the Streamlit app is on `.streamlit.app`
 *    which we don't control) — the risk_score is clamped server-side.
 *  - Falls back to a manual "Log my result" form so patients can still
 *    write the outcome to their Manodayam timeline even if the iframe
 *    can't postMessage.
 */

const STREAMLIT_URL = "https://manodayam-voice-screening-pfv4kkm9etktu4egavhvt5.streamlit.app/";
const STREAMLIT_EMBED_URL = `${STREAMLIT_URL}?embed=true`;

const BAND_OPTIONS = [
  { value: "normal",   label: "Feeling steady — no clear concerns", risk: 15 },
  { value: "mild",     label: "Some mild indicators noticed",       risk: 35 },
  { value: "moderate", label: "Moderate concern",                   risk: 60 },
  { value: "high",     label: "High severity — I need support",     risk: 85 },
];

export default function VoiceScreeningEmbed() {
  const [logged, setLogged] = useState(null); // last logged report
  const [saving, setSaving] = useState(false);
  const [choice, setChoice] = useState("");
  const [notes, setNotes] = useState("");

  // Bridge: listen for a well-formed screening result from the iframe.
  useEffect(() => {
    const onMsg = async (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;
      if (data.type !== "manodayam:screening_result") return;
      const risk = Number(data.risk_score);
      if (!Number.isFinite(risk)) return;
      try {
        const { data: report } = await api.post("/screening/streamlit-result", {
          category: data.category === "cognitive" ? "cognitive" : "mental_health",
          risk_score: Math.max(0, Math.min(100, risk)),
          band: data.band || undefined,
          confidence: typeof data.confidence === "number" ? data.confidence : undefined,
          summary: (data.summary || "").toString().slice(0, 600) || undefined,
          model_version: data.model_version || "streamlit-v1",
        });
        setLogged(report);
        toast.success("Screening result saved to your timeline");
      } catch (e) {
        toast.error(extractError(e));
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const logManually = async () => {
    const pick = BAND_OPTIONS.find((b) => b.value === choice);
    if (!pick) {
      toast.error("Pick how you felt after the screening");
      return;
    }
    setSaving(true);
    try {
      const { data: report } = await api.post("/screening/streamlit-result", {
        category: "mental_health",
        risk_score: pick.risk,
        band: pick.value,
        summary: notes.trim().slice(0, 600) || undefined,
        model_version: "streamlit-v1-manual",
      });
      setLogged(report);
      setChoice("");
      setNotes("");
      toast.success("Check-in saved to your timeline");
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="voice-screening-section"
      className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6 lg:p-8 space-y-5"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-[color:var(--mano-hero)]/10 flex items-center justify-center text-[color:var(--mano-hero)] shrink-0">
          <Mic size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h1
            data-testid="voice-screening-title"
            className="font-display text-2xl sm:text-3xl leading-tight text-[color:var(--mano-ink)]"
          >
            Voice check-in
          </h1>
          <p
            data-testid="voice-screening-subtitle"
            className="mt-2 text-sm sm:text-base text-[color:var(--mano-ink-soft)] leading-relaxed max-w-3xl"
          >
            Record or upload a short voice sample describing your daily routine.
            The tool provides a screening-level indication of anxiety /
            depression-related symptoms — and when you finish, we'll save
            the result to your Manodayam timeline automatically.
          </p>
        </div>
      </div>

      <div
        data-testid="voice-screening-disclaimer"
        className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
      >
        <AlertCircle size={16} className="text-amber-700 mt-0.5 shrink-0" />
        <p className="text-xs sm:text-sm text-amber-900 leading-relaxed">
          <b>This is not a clinical diagnosis.</b> It is only a speech-based
          screening tool.
        </p>
      </div>

      <div
        data-testid="voice-screening-frame-wrap"
        className="rounded-2xl overflow-hidden border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)]"
      >
        <iframe
          data-testid="voice-screening-iframe"
          src={STREAMLIT_EMBED_URL}
          title="Manodayam Voice Screening"
          allow="microphone; camera; clipboard-read; clipboard-write"
          className="w-full block h-[1800px] md:h-[1500px] border-0"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-[color:var(--mano-ink-soft)]">
          Having trouble with the embedded tool? Open it in a new tab.
        </p>
        <a
          href={STREAMLIT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="voice-screening-open-new-tab"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold text-sm hover:bg-[color:var(--mano-primary-hover)] transition-colors"
        >
          <ExternalLink size={14} /> Open Screening Tool in New Tab
        </a>
      </div>

      {/* Manual log form — kept as fallback for iframes that can't postMessage */}
      <div
        data-testid="screening-manual-log"
        className="rounded-2xl border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)] p-5 space-y-3"
      >
        <div className="flex items-center gap-2">
          <ListChecks size={16} className="text-[color:var(--mano-primary)]" />
          <div className="font-semibold text-[color:var(--mano-ink)]">
            Finished your check-in? Save it to your timeline
          </div>
        </div>
        <p className="text-xs text-[color:var(--mano-ink-soft)]">
          If the screening app didn't auto-save your result, pick the band
          that matches what you saw and we'll log it against today.
        </p>
        <div className="grid gap-2">
          {BAND_OPTIONS.map((b) => (
            <label
              key={b.value}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${
                choice === b.value
                  ? "border-[color:var(--mano-primary)] bg-white"
                  : "border-transparent bg-white hover:border-[color:var(--mano-border)]"
              }`}
              data-testid={`screening-band-${b.value}`}
            >
              <input
                type="radio"
                name="screening-band"
                value={b.value}
                checked={choice === b.value}
                onChange={() => setChoice(b.value)}
                className="accent-[color:var(--mano-primary)]"
              />
              <span className="text-sm text-[color:var(--mano-ink)]">{b.label}</span>
            </label>
          ))}
        </div>
        <textarea
          data-testid="screening-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything you'd like your care team to know? (optional)"
          rows={2}
          className="w-full rounded-xl border border-[color:var(--mano-border)] bg-white px-3 py-2 text-sm"
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-[color:var(--mano-ink-soft)]">
            You can view every saved check-in on{" "}
            <Link
              to="/voicelab"
              className="text-[color:var(--mano-primary)] font-semibold hover:underline"
              data-testid="screening-view-timeline"
            >
              My check-ins →
            </Link>
          </p>
          <button
            data-testid="screening-save-btn"
            onClick={logManually}
            disabled={saving || !choice}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold text-sm hover:bg-[color:var(--mano-primary-hover)] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {saving ? "Saving…" : "Save this check-in"}
          </button>
        </div>
        {logged && (
          <div
            data-testid="screening-logged-confirm"
            className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-center gap-2"
          >
            <CheckCircle2 size={14} className="text-emerald-600" />
            Check-in logged · {logged.band} · risk {logged.risk_score}/100
          </div>
        )}
      </div>
    </section>
  );
}
